import React, { useEffect, useMemo, useState } from 'react'
import {
  Generator,
  NewLiveValueBatteryFilled,
  NewLiveValueHomeConsumption,
  NewLiveValueProducer,
  NewLiveValueSun,
  NewLiveValueWallbox,
  NewHagerLiveValueWallbox,
} from '@e3dc-react/icons'
import { GuiLiveViewItemModel } from 'src/Libs/live-view'
import { getConsumptionPower } from 'src/Libs/live-view/utils/calculations'
import { LiveDataModel, PowermeterHardwareModel } from 'src/State/Storage/StorageModel'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import StraightArrowShort from 'src/Elements/LiveView/AlternativeView/Arrows/StraightArrowShort'
import CurvedArrow from 'src/Elements/LiveView/AlternativeView/Arrows/CurvedArrow'
import { useBaseLiveValueItems } from './use-base-live-value-items'
import SideArrow from 'src/Elements/LiveView/AlternativeView/Arrows/SideArrow'
import CurvedSideArrow from 'src/Elements/LiveView/AlternativeView/Arrows/CurvedSideArrow'
import { useMediaQuery, useTheme } from '@mui/system'
import CurvedSecondLevelArrow from 'src/Elements/LiveView/AlternativeView/Arrows/CurvedSecondLevelArrow'
import SecondLevelArrow from 'src/Elements/LiveView/AlternativeView/Arrows/SecondLevelArrow'
import { liveDataValueFormatter } from 'src/Elements/LiveView/LiveView'
import { makeStyles } from 'tss-react/mui'
import { useResponsiveHelpers } from './use-responsive-helpers'
import { isFlowTheme } from 'src/Libs/utils'
import { Box } from '@mui/material'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { ChristmasTreeSvg, EasterBunny } from 'src/Elements/LiveView/AlternativeView/Arrows/Svg/Themes'

const useStyles = makeStyles<{ soc: number }>()((theme, { soc }) => ({
  batteryIcon: {
    position: 'relative',

    '&:after': {
      content: `"${soc} %"`,
      position: 'absolute',
      inset: 0,
      transform: 'translateX(-1px)',
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1,
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.primary,
      display: 'flex',
      flexWrap: 'nowrap',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
}))

interface GuiLiveViewItemsProps {
  batteryHomePower: number
  homeBatteryPower: number
  pvHomePower: number
  networkHomePower: number
  homeNetworkPower: number
  consumption: number
  homeConsumerPower: number
  socCurrent: number
  consumers?: PowermeterHardwareModel[]
  producers?: PowermeterHardwareModel[]
  wallboxes?: WallboxHardwareModel[]
  liveData?: LiveDataModel
  offlineClientStorageIds?: string[]
  offlineIcon?: React.ReactNode
  homeWallboxPower: number
  trackerHomePower: number
  shallowLiveView?: boolean
  isBlockingTimeActive?: boolean
  isDynamicPowerPriceEnabled?: boolean
  showDynamicPrices?: boolean
  storageId?: string
}

/**
 * Hook that returns the live view items for the GUI alternative view.
 */
export const useGuiLiveValueItems = ({
  batteryHomePower,
  homeBatteryPower,
  pvHomePower,
  networkHomePower,
  homeNetworkPower,
  consumption,
  homeConsumerPower,
  homeWallboxPower,
  consumers,
  liveData,
  offlineClientStorageIds,
  offlineIcon,
  producers,
  trackerHomePower,
  shallowLiveView,
  wallboxes,
  isBlockingTimeActive,
  isDynamicPowerPriceEnabled,
  showDynamicPrices,
  storageId,
  socCurrent,
}: GuiLiveViewItemsProps): { items: GuiLiveViewItemModel[]; subLevelItems: GuiLiveViewItemModel[] } => {
  const {
    batteryLiveItem,
    pvHomePowerLiveItem,
    evsesSubLiveItem,
    homeConsumptionLiveItem,
    externalConsumerSubLiveItem,
    consumptionPowerLiveItem,
    externalProduction,
    networkHomePowerLiveItem,
  } = useBaseLiveValueItems({
    batteryHomePower,
    homeBatteryPower,
    pvHomePower,
    consumption,
    homeConsumerPower,
    homeWallboxPower,
    consumers,
    liveData,
    offlineClientStorageIds,
    offlineIcon,
    producers,
    trackerHomePower,
    wallboxes,
    networkHomePower,
    homeNetworkPower,
    isBlockingTimeActive,
    isDynamicPowerPriceEnabled,
    showDynamicPrices,
    storageId,
  })

  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const stateOfCharge = Math.max(0, Math.min(socCurrent, 100))
  const getResponsiveValues = useResponsiveHelpers()
  const {
    featureFlags: { isChristmasThemeEnabled, isEasterThemeEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const sideItemOffset = getResponsiveValues(-50, -40, -50, -47)
  const centerItemOffset = getResponsiveValues(-17, -7, -15, -12)

  const consumerCount = consumers?.length ?? 0
  const producerCount = producers?.length ?? 0
  const wallboxCount = wallboxes?.length ?? 0
  const { classes } = useStyles({ soc: stateOfCharge })

  const [homeConsumptionPowerLiveItemChildren, setHomeConsumptionPowerLiveItemChildren] = useState<GuiLiveViewItemModel[]>([])

  const guiBatteryLiveItem: GuiLiveViewItemModel = useMemo(() => {
    const directionIsReversed = (homeBatteryPower ?? 0) > 0

    return {
      ...batteryLiveItem,
      directionIsReversed,
      icon: (
        <NewLiveValueBatteryFilled
          sx={{ height: '100%', width: '100%' }}
          color={theme.palette.liveView.batteryIconFill}
          stateOfCharge={stateOfCharge}
        />
      ),
      position: 'bottom-left',
      animationComponent: (
        <CurvedArrow directionIsReversed={directionIsReversed} doNotFlow={batteryLiveItem.doNotFlow} position={'bottom-left'} sx={{ left: 3 }} />
      ),
      contentSx: {
        right: getResponsiveValues(0, '30%', '30%'),
      },
      sx: {
        gridRow: 3,
        gridColumn: 1,
        alignSelf: 'flex-start',
        justifySelf: getResponsiveValues('flex-start', 'flex-end', 'flex-end'),
        top: sideItemOffset,
      },
      iconPosition: 'bottom',
      iconClassName: classes.batteryIcon,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batteryLiveItem, homeBatteryPower, stateOfCharge, getResponsiveValues, sideItemOffset])

  const guiPvHomePowerLiveItem: GuiLiveViewItemModel = useMemo(() => {
    const doNotFlow = !pvHomePower
    const directionIsReversed = false

    return {
      ...pvHomePowerLiveItem,
      icon: <NewLiveValueSun sx={{ height: '100%', width: '100%' }} />,
      doNotFlow,
      directionIsReversed,
      position: 'top-left',
      animationComponent: <CurvedArrow doNotFlow={doNotFlow} directionIsReversed={directionIsReversed} position={'top-left'} sx={{ left: 3 }} />,
      contentSx: {
        right: getResponsiveValues(0, '30%', '35%'),
      },
      sx: {
        gridRow: 1,
        gridColumn: 1,
        alignSelf: 'flex-end',
        justifySelf: getResponsiveValues('flex-start', 'flex-end', 'flex-end'),
        bottom: sideItemOffset,
      },
      iconPosition: 'top',
    }
  }, [pvHomePower, pvHomePowerLiveItem, getResponsiveValues, sideItemOffset])

  const guiNetworkHomePowerLiveItem: GuiLiveViewItemModel = useMemo(() => {
    const doNotFlow = !networkHomePower && !homeNetworkPower
    const directionIsReversed = (homeNetworkPower ?? 0) > 0

    return {
      ...networkHomePowerLiveItem,
      doNotFlow,
      directionIsReversed,
      position: 'top-right',
      animationComponent: <CurvedArrow doNotFlow={doNotFlow} directionIsReversed={directionIsReversed} position={'top-right'} sx={{ right: 3 }} />,
      contentSx: {
        right: getResponsiveValues(0, '-18px', '-33%'),
      },
      sx: {
        gridRow: 1,
        gridColumn: 3,
        alignSelf: 'flex-end',
        justifySelf: getResponsiveValues('flex-end', 'flex-start', 'flex-start'),
        bottom: sideItemOffset,
      },
      iconPosition: 'top',
    }
  }, [homeNetworkPower, networkHomePower, networkHomePowerLiveItem, getResponsiveValues, sideItemOffset])

  const guiWallboxItem: GuiLiveViewItemModel | undefined = useMemo(() => {
    const doNotFlow = !homeWallboxPower
    const directionIsReversed = homeWallboxPower > 0
    const WallboxIcon = isFlowTheme ? NewHagerLiveValueWallbox : NewLiveValueWallbox

    return wallboxCount && evsesSubLiveItem
      ? {
          ...evsesSubLiveItem,
          icon: <WallboxIcon sx={{ height: '100%', width: '100%' }} />,
          doNotFlow: doNotFlow,
          directionIsReversed: directionIsReversed,
          animationComponent: <StraightArrowShort doNotFlow={doNotFlow} directionIsReversed={directionIsReversed} />,
          position: 'bottom-center',
          sx: {
            gridRow: 3,
            gridColumn: 2,
            alignSelf: 'flex-start',
            justifySelf: 'center',
            top: centerItemOffset,
          },
          iconPosition: 'bottom',
        }
      : undefined
  }, [wallboxCount, homeWallboxPower, evsesSubLiveItem, centerItemOffset])

  const guiExternalConsumerSubLiveItem: GuiLiveViewItemModel | undefined = useMemo(
    () =>
      consumerCount && externalConsumerSubLiveItem
        ? {
            ...externalConsumerSubLiveItem,
            icon: <Generator sx={{ height: '100%', width: '100%', padding: '9px' }} />,
            directionIsReversed: false,
            position: 'sub-first',
            contentSx: upMd
              ? {
                  right: -6,
                }
              : {},
            sx: {
              gridRow: upMd ? 3 : 'unset',
              gridColumn: upMd ? 4 : 'unset',
              display: 'flex',
              flexDirection: upMd ? 'row' : 'column',
              right: getResponsiveValues(0, 10, -34, 0, 24),
              top: upMd ? -3 : 0,
              alignSelf: upMd ? 'inherit' : 'flex-end',
              justifySelf: upMd ? 'inherit' : 'center',
              alignItems: getResponsiveValues('center', 'center', 'flex-start', 'inherit', 'center'),
            },
            animationComponent: upMd ? (
              <SideArrow doNotFlow={externalConsumerSubLiveItem.doNotFlow} directionIsReversed={true} sx={{ top: 17, marginLeft: 2 }} />
            ) : (
              <CurvedSecondLevelArrow
                doNotFlow={externalConsumerSubLiveItem.doNotFlow}
                directionIsReversed={true}
                sx={{ right: getResponsiveValues(-40, -50, -20, undefined, -60) }}
              />
            ),
            iconPosition: 'bottom',
          }
        : undefined,
    [consumerCount, externalConsumerSubLiveItem, upMd, getResponsiveValues]
  )

  const homeConsumption = useMemo<number>(() => (consumption ?? 0) - (homeConsumerPower ?? 0), [homeConsumerPower, consumption])

  const guiHomeConsumptionItem: GuiLiveViewItemModel = useMemo(() => {
    const directionIsReversed = true
    const doNotFlow = !homeConsumption

    const subItemStyles = {
      position: 'relative',
      gridRow: upMd ? 2 : 4,
      gridColumn: upMd ? 4 : 3,
      display: 'flex',
      flexDirection: upMd ? 'row' : 'column',
      right: getResponsiveValues(0, 0, 0),
      top: upMd ? 47 : 0,
      alignSelf: 'flex-end',
      justifySelf: getResponsiveValues('flex-start', 'flex-end', 'flex-start', 'flex-end'),
      alignItems: upMd ? 'flex-start' : 'inherit',
      marginLeft: upMd ? '16px' : 0,
    }

    const animationComponentForSubItems = upMd ? (
      <CurvedSideArrow doNotFlow={!homeConsumption} directionIsReversed={directionIsReversed} sx={{ bottom: -50, left: 62 }} />
    ) : (
      <SecondLevelArrow doNotFlow={!homeConsumption} directionIsReversed={directionIsReversed} />
    )

    const renderIcon = (): React.ReactElement => {
      if ((isEasterThemeEnabled || isChristmasThemeEnabled) && !isFeatureFlagsLoading) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {isChristmasThemeEnabled && <ChristmasTreeSvg style={{ position: 'absolute', bottom: 4, right: 0 }} />}
            {isEasterThemeEnabled && (
              <EasterBunny isFlowTheme={isFlowTheme} style={{ position: 'absolute', bottom: -4, right: -12, width: '40px', height: '40px' }} />
            )}
            <NewLiveValueHomeConsumption sx={{ height: '100%', width: '100%' }} />
          </Box>
        )
      }

      return <NewLiveValueHomeConsumption sx={{ height: '100%', width: '100%' }} />
    }

    return {
      ...homeConsumptionLiveItem,
      icon: renderIcon(),
      doNotFlow,
      directionIsReversed,
      position: guiExternalConsumerSubLiveItem ? 'sub-second' : 'bottom-right',
      animationComponent: guiExternalConsumerSubLiveItem ? (
        animationComponentForSubItems
      ) : (
        <CurvedArrow doNotFlow={doNotFlow} directionIsReversed={directionIsReversed} position={'bottom-right'} sx={{ right: 3 }} />
      ),
      contentSx: {
        right: guiExternalConsumerSubLiveItem ? 'inherit' : getResponsiveValues(0, '-18px', '-33%'),
      },
      sx: guiExternalConsumerSubLiveItem
        ? subItemStyles
        : {
            gridRow: 3,
            gridColumn: 3,
            alignSelf: 'flex-start',
            justifySelf: getResponsiveValues('flex-end', 'flex-start', 'flex-start'),
            top: sideItemOffset,
          },
      iconPosition: 'bottom',
    }
  }, [
    homeConsumption,
    upMd,
    getResponsiveValues,
    homeConsumptionLiveItem,
    guiExternalConsumerSubLiveItem,
    sideItemOffset,
    isEasterThemeEnabled,
    isChristmasThemeEnabled,
    isFeatureFlagsLoading,
  ])

  useEffect(() => {
    const item: GuiLiveViewItemModel[] = []
    if (guiExternalConsumerSubLiveItem) {
      item.push(guiExternalConsumerSubLiveItem)
      item.push(guiHomeConsumptionItem)
    }
    setHomeConsumptionPowerLiveItemChildren(item)
    return () => {
      setHomeConsumptionPowerLiveItemChildren([])
    }
  }, [guiExternalConsumerSubLiveItem, guiHomeConsumptionItem])

  const consumptionPower = useMemo(() => getConsumptionPower(consumption, homeWallboxPower), [consumption, homeWallboxPower])
  const consumptionPowerDoNotFlow = !consumptionPower
  const consumptionPowerDirectionIsReversed = consumptionPower < 0

  const guiConsumptionPowerLiveItem: GuiLiveViewItemModel = {
    ...consumptionPowerLiveItem,
    value: liveDataValueFormatter(consumption),
    doNotFlow: consumptionPowerDoNotFlow,
    directionIsReversed: false,
    position: 'bottom-right',
    contentSx: {
      right: getResponsiveValues(0, -17, '-30%'),
    },
    sx: {
      gridRow: 3,
      gridColumn: 3,
      alignSelf: 'flex-start',
      justifySelf: getResponsiveValues('flex-end', 'flex-start', 'flex-start'),
      top: sideItemOffset,
    },
    iconPosition: 'bottom',
    animationComponent: (
      <CurvedArrow
        doNotFlow={consumptionPowerDoNotFlow}
        directionIsReversed={!consumptionPowerDirectionIsReversed}
        position={'bottom-right'}
        sx={{ right: 3 }}
      />
    ),
  }

  const guiExternalProduction: GuiLiveViewItemModel = useMemo(() => {
    const doNotFlow = !trackerHomePower
    const directionIsReversed = true

    return {
      ...externalProduction,
      doNotFlow,
      icon: <NewLiveValueProducer sx={{ height: '100%', width: '100%' }} />,
      directionIsReversed,
      position: 'top-center',
      animationComponent: <StraightArrowShort doNotFlow={doNotFlow} directionIsReversed={directionIsReversed} sx={{ top: '9px' }} />,
      sx: {
        gridRow: 1,
        gridColumn: 2,
        alignSelf: 'flex-end',
        justifySelf: 'center',
        bottom: centerItemOffset,
      },
      iconPosition: 'top',
    }
  }, [trackerHomePower, externalProduction, centerItemOffset])

  const items: GuiLiveViewItemModel[] = []
  const subLevelItems: GuiLiveViewItemModel[] = homeConsumptionPowerLiveItemChildren

  const addLiveViewItems = (): void => {
    if (shallowLiveView) {
      if (producerCount) items.push(guiExternalProduction)
      items.push(guiPvHomePowerLiveItem, guiNetworkHomePowerLiveItem, guiBatteryLiveItem)
      items.push(homeConsumptionPowerLiveItemChildren.length > 0 ? guiConsumptionPowerLiveItem : guiHomeConsumptionItem)
      if (guiWallboxItem) items.push(guiWallboxItem)
    } else {
      if (producerCount) items.push(guiExternalProduction)
      items.push(guiPvHomePowerLiveItem, guiNetworkHomePowerLiveItem, guiBatteryLiveItem)
      if (guiWallboxItem) items.push(guiWallboxItem)
      items.push(homeConsumptionPowerLiveItemChildren.length > 0 ? guiConsumptionPowerLiveItem : guiHomeConsumptionItem)
    }
  }

  addLiveViewItems()

  return { items, subLevelItems }
}
